import { useQuery } from "@tanstack/react-query";
import {
  buildObservabilityData,
  isObservabilityContractMissing,
  backendHasObservabilityContract,
  type ObservabilityData,
  type Period,
  type BackendObservabilityEvent,
  type BackendObservabilityResponse,
  type BackendObservabilitySummary,
  type BackendObservabilitySummaryResponse,
} from "@/services/observability";
import {
  OBSERVABILITY_QUERY,
  OBSERVABILITY_SUMMARY_QUERY,
  EVENTS_PAGE_SIZE,
  EVENTS_MAX_PAGES,
  sinceForPeriod,
} from "@/services/observability";
import { graphqlRequest } from "@/services/deployment/client";

/**
 * Every event in the window, walked a page at a time.
 *
 * The server caps a page at 500 (`ObservabilityContext.@max_limit`), so one
 * request cannot answer for a busy org. Stops as soon as a short page proves the
 * window is exhausted, and never runs past `EVENTS_MAX_PAGES` — this feeds a
 * screen, and an org with a 90d window should not hold it open for dozens of
 * round trips.
 */
async function fetchEventPages(
  organizationId: string,
  since: string,
): Promise<{ events: BackendObservabilityEvent[]; truncated: boolean }> {
  const events: BackendObservabilityEvent[] = [];
  for (let page = 0; page < EVENTS_MAX_PAGES; page += 1) {
    const response = await graphqlRequest<BackendObservabilityResponse>(OBSERVABILITY_QUERY, {
      organizationId,
      since,
      limit: EVENTS_PAGE_SIZE,
      offset: page * EVENTS_PAGE_SIZE,
    });
    const batch = response.observabilityEvents ?? [];
    events.push(...batch);
    // A short page is the end of the window — asking for the next one returns [].
    if (batch.length < EVENTS_PAGE_SIZE) return { events, truncated: false };
  }
  return { events, truncated: true };
}

export function useObservabilityEvents(orgId: string | null, period: Period) {
  return useQuery<ObservabilityData>({
    queryKey: ["observability", orgId, period],
    queryFn: async () => {
      try {
        const hasContract = await backendHasObservabilityContract();
        if (!hasContract) {
          return { ...buildObservabilityData([], orgId!, period), contractUnavailable: true };
        }
        const since = sinceForPeriod(period);

        /*
         * TWO reads, deliberately.
         *
         * The summary is ONE uncapped SQL aggregate and owns every figure that
         * adds up — cost, tokens, run and failure counts. The event feed is
         * paged and owns the rows: the runs table, the per-route and per-user
         * breakdowns, latency percentiles.
         *
         * They are not redundant. Summing the feed is only right while it holds
         * the whole window, and it does not for a busy org: dev 2026-09-21, org
         * 59b63e2f had 1942 events, the single 500-row page reached back only to
         * 04:14:02, and the tab showed $2.096740 against the $2.816774 the
         * aggregate reports — 25.6% short, against the Usage tab which had
         * always read the aggregate. Both read `observability_events`; the only
         * difference was where the sum happened.
         *
         * A summary failure must not blank the tab, so it degrades to the
         * feed-derived totals (right for a small org, low for a large one) —
         * strictly better than showing nothing.
         */
        const [summaryResult, pages] = await Promise.all([
          graphqlRequest<BackendObservabilitySummaryResponse>(OBSERVABILITY_SUMMARY_QUERY, {
            organizationId: orgId,
            since,
          }).catch(() => null),
          fetchEventPages(orgId!, since),
        ]);

        const summary: BackendObservabilitySummary | null =
          summaryResult?.observabilitySummary ?? null;

        return buildObservabilityData(pages.events, orgId!, period, summary);
      } catch (error) {
        if (isObservabilityContractMissing(error)) {
          return { ...buildObservabilityData([], orgId!, period), contractUnavailable: true };
        }
        throw error;
      }
    },
    enabled: Boolean(orgId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
